/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IDropdownOption } from '@fluentui/react/lib/Dropdown';
import { Icon } from '@fluentui/react/lib/Icon';
import { Stack } from '@fluentui/react/lib/Stack';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme, getStylist } from 'src/core/themes/luisStylist';
import { YAxisMode } from 'src/luis/projects/analytics/components/PredictionsPerIntentCard/PredictionsPerIntentCard';
import { AnalyticsDropdown } from 'src/luis/projects/analytics/components/TrainingEvaluation/AnalyticsDropdown';
import { SortOptions } from 'src/luis/projects/analytics/types/SortOptions';

export type SortDropdownProps = {
    /**
     * The y axis mode either numbers or percentages.
     */
    yAxisMode: YAxisMode;

    /**
     * The currently selected option key.
     */
    selected: string;

    /**
     * Callback invoked when the dropdown selection changes.
     */
    onChange: (option: IDropdownOption) => void;

    /**
     * Aria label to be added to the dropdown component.
     */
    ariaLabel?: string;
};

const messages = defineMessages({
    correctSortingOpt: {
        id: 'analytics.predictionsPerIntentCard.correctSortingOpt',
        defaultMessage: 'Correctly predicted'
    },
    unclearSortingOpt: {
        id: 'analytics.predictionsPerIntentCard.unclearSortingOpt',
        defaultMessage: 'Unclear'
    },
    incorrectSortingOpt: {
        id: 'analytics.predictionsPerIntentCard.incorrectSortingOpt',
        defaultMessage: 'Incorrectly predicted'
    },
    nameSortingOpt: {
        id: 'analytics.predictionsPerIntentCard.nameSortingOpt',
        defaultMessage: 'Name, A to Z'
    },
    numberSortingOpt: {
        id: 'analytics.predictionsPerIntentCard.numberSortingOpt',
        defaultMessage: 'Number of utterances'
    },
    placeholderSortingDropdown: {
        id: 'analytics.predictionsPerIntentCard.placeholderSortingDropdown',
        defaultMessage: 'Sort intents by'
    }
});

const { getClassName } = getStylist('Dashboard');
const dashboardTheme = getScopedTheme('Dashboard');

const rootClassName = getClassName('StyledAnalyticsDropdown', theme => ({
    border: 'none',
    height: theme.dropdownLargeHeight
}));

const titleClassName = getClassName('Title', theme => ({
    paddingLeft: 0,
    fontSize: theme.labelFontSize,
    height: theme.dropdownLargeHeight
}));

/**
 * The dropdown of the sorting options for the data in the distribution chart.
 *
 * @param props The properties of the sorting dropdown.
 */
export const SortDropdown = (props: SortDropdownProps) => {
    const { selected, onChange, yAxisMode, ariaLabel } = props;
    const { localize } = useLocalization();

    const sortOptions: IDropdownOption[] = [
        { key: SortOptions.correct, text: localize(messages.correctSortingOpt) },
        { key: SortOptions.unclear, text: localize(messages.unclearSortingOpt) },
        { key: SortOptions.incorrect, text: localize(messages.incorrectSortingOpt) },
        { key: SortOptions.intentName, text: localize(messages.nameSortingOpt) },
        { key: SortOptions.allUtterances, text: localize(messages.numberSortingOpt), disabled: yAxisMode === 'percent' }
    ];

    return (
        <AnalyticsDropdown
            isSearchEnabled={false}
            ariaLabel={ariaLabel}
            options={sortOptions}
            selectedKey={selected}
            onChange={(_, option) => onChange(option)}
            placeholder={localize(messages.placeholderSortingDropdown)}
            styles={{ title: titleClassName, root: rootClassName, caretDown: { color: dashboardTheme.caretColor, fontWeight: 'bold' } }}
            onRenderTitle={options => {
                const opt = options[0];

                return (
                    <Stack horizontal tokens={{ childrenGap: 10 }} horizontalAlign="center">
                        <Icon iconName="Refresh" />
                        <span>{opt.text}</span>
                    </Stack>
                );
            }}
        />
    );
};
